import { Tooltip } from '@mantine/core';
import { CalendarX, Video } from 'lucide-react';
import { FC, ReactElement } from 'react';
import { validate } from 'uuid';

import CircularLoader from '@/components/CircularLoader.tsx';
import { TicketNumberPill } from '@/components/PatientModal/ticket-number-pill.tsx';
import StatusDot from '@/components/StatusDot.tsx';
import { AppointmentPill, getCollectionPillColor } from '@/components/appointment-pill.tsx';
import Paragraph from '@/components/text/Paragraph.tsx';

import { useTicketEditor } from '@/hooks/useTicketEditor.tsx';

import StatusDotType from '@/models/StatusDotType.ts';
import { DailyAppointment, Insurance } from '@/models/appointment/Appointment.ts';
import { UserAppointmentDto } from '@/models/call-system/dtos/user-appointment-dto.ts';

import { isCheckedIn, isCheckedOut, isCheckinArrived, isCheckinPending } from '@/utils/appointmentUtils.ts';
import { dayjs } from '@/utils/dayjsSetup.ts';
import { getInsuranceColor } from '@/utils/insurance-utils.ts';
import { cn } from '@/utils/styleUtils.ts';

interface Props {
    patientName?: string;
    insurance?: Insurance;
    isInsuranceCardActive: boolean;
    checkinData?: UserAppointmentDto;
    date: string;
    showDate?: boolean;
    loading: boolean;
    isOnline?: boolean;
    isDischarged?: boolean;
    isNoShow?: boolean;
    appointment?: DailyAppointment;
    onTicketUpdate?: () => Promise<void>;
}

const AppointmentCardHeader: FC<Props> = ({
    patientName,
    insurance,
    isInsuranceCardActive,
    checkinData,
    date,
    showDate = false,
    loading,
    isOnline = false,
    isNoShow = false,
    appointment,
    onTicketUpdate,
}) => {
    const name = patientName ?? 'Nicht angegeben';
    const is_ticket_displayed = checkinData?.number && !validate(checkinData.number);

    const can_edit_ticket = Boolean(
        is_ticket_displayed &&
        appointment &&
        checkinData &&
        !isCheckedOut(checkinData?.appointment?.checkout_status) &&
        onTicketUpdate
    );

    const { handleEditTicket } = useTicketEditor({
        appointment: appointment!,
        checkinData: checkinData!,
        onTicketUpdate: onTicketUpdate!,
    });

    let status_dot: StatusDotType | undefined;
    let status_text: string = '';

    if (isCheckedOut(checkinData?.appointment?.checkout_status)) {
        status_dot = StatusDotType.BLUE;
        status_text = 'Abgemeldet'; //"Checked out"
    } else if (isCheckinPending(checkinData?.appointment?.checkin_status)) {
        status_dot = undefined;
        status_text = '';
    } else if (isCheckinArrived(checkinData?.appointment?.checkin_status)) {
        status_dot = StatusDotType.YELLOW;
        status_text = `Angekommen mit Ticket: ${checkinData?.number}`; // "Arrived"
    } else if (isCheckedIn(checkinData?.appointment?.checkin_status)) {
        status_dot = StatusDotType.GREEN;
        status_text = `Eingecheckt mit Ticket: ${checkinData?.number}`; // "Checked-in with ticket"
    } else {
        status_dot = StatusDotType.RED;
        status_text = 'No-show';
    }

    const renderStatus = (): ReactElement | undefined => {
        if (isOnline) return <Video />;
        if (status_dot) return <StatusDot type={status_dot} text={status_text} />;
        return undefined;
    };

    return (
        <div className="flex items-baseline justify-between gap-2">
            {loading && <CircularLoader size="sm" />}
            <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-1 lg:flex-nowrap xl:gap-3">
                <Paragraph
                    className={cn(
                        'text-sm font-semibold underline xl:text-base',
                        !patientName && 'text-slate-400 no-underline'
                    )}
                >
                    {loading ? 'Wird geladen' : name}
                </Paragraph>
                {appointment?.collection_data && (
                    <AppointmentPill
                        text={`${appointment.collection_data.position}/${appointment.collection_data.total_appointments}`}
                        color={getCollectionPillColor(appointment)}
                        variant="filled"
                    />
                )}
                <div className="flex items-center gap-1 xl:gap-3">
                    {insurance && (
                        <AppointmentPill text={insurance} color={getInsuranceColor(isInsuranceCardActive, insurance)} />
                    )}
                    {is_ticket_displayed && checkinData?.number && (
                        <TicketNumberPill
                            ticketNumber={checkinData.number}
                            isEditable={can_edit_ticket}
                            onEdit={handleEditTicket}
                        />
                    )}
                    {renderStatus()}
                    {isNoShow && (
                        <Tooltip label="No-show">
                            <CalendarX className="size-5 text-red-600" />
                        </Tooltip>
                    )}
                </div>
            </div>
            {showDate && (
                <Paragraph className="text-xs text-slate-500 xl:text-sm">{dayjs(date).format('HH:mm')}</Paragraph>
            )}
        </div>
    );
};

export default AppointmentCardHeader;
